import { Badge, Button, Tabs, TabsProps } from 'antd';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { defaultSidebarWidth, narrowSidebarWidth, setIsResizing, setSelectedKeys, setSideBarWidth } from 'store/slices/windowStateSlice';
import ChatTab from './ChatTab/ChatTab';
import ContactsTab from './ContactsTab/ContactsTab';
import RoomsTab from './RoomsTab/RoomsTab';
import classes from './SideBar.module.scss';
import { clearReduxFunction } from 'store/reducers/userDataCreator';
import Assets from 'assets/icons';
import { checkIsTariffEnded, checkIsAwaitingPayment } from 'helpers/tariffPayment';
import { serializeFiltersToUrl } from 'helpers/documentFiltersUrl';

const isBillingActive = window.REACT_APP_IS_BILLING_ACTIVE !== 'REPLACE_REACT_APP_IS_BILLING_ACTIVE' ? window.REACT_APP_IS_BILLING_ACTIVE : process.env.REACT_APP_IS_BILLING_ACTIVE;

export default function SideBar() {
  const sidebarRef = React.useRef<HTMLDivElement>(null);
  const [selectedTab, setSelectedTab] = React.useState('rooms')

  const { selectedKeys, isResizing, sideBarWidth } = useAppSelector((store) => store.windowState);
  const { userData, available_rooms } = useAppSelector((store) => store.userData);
  const { dataRoom } = useAppSelector((store) => store.dataRoom);
  const { roomAllChats, globalChats } =  useAppSelector((state) => state.chat);  
  const { appliedFilters } = useAppSelector((store) => store.documents);  

  const isMobile = /Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(navigator.userAgent)
  // const isLoadingSomeData = isLoadingUserData || isLoadingRoom;
  const isBlockedRoom = isBillingActive && dataRoom && (checkIsTariffEnded(dataRoom) || checkIsAwaitingPayment(dataRoom));
  const isRoomsSection = ['documents','rooms','logs','nda','users', 'data-analytics', 'tariffs'].includes(selectedKeys[0]);
  const isVisibleHideSideBarButton = available_rooms[0] && isRoomsSection;
  const isFirstRender = React.useRef(true);
  
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const startResizing = React.useCallback((mouseDownEvent: any) => {
    dispatch(setIsResizing(true));
  }, []);

  const stopResizing = React.useCallback(() => {
    dispatch(setIsResizing(false));
  }, []);

  const collapseClick = () => {
    if (sideBarWidth === 64) {
      localStorage.setItem('sideBarWidth', String(defaultSidebarWidth));
      dispatch(setSideBarWidth(defaultSidebarWidth));
    } else {
      localStorage.setItem('sideBarWidth', String(64));
      dispatch(setSideBarWidth(64));
    }
  }

  const resize = React.useCallback(
    (mouseMoveEvent: any) => {
      if (isResizing) {
        const widthOfSidebar = mouseMoveEvent.clientX - sidebarRef.current!.getBoundingClientRect().left;        
        if (defaultSidebarWidth <= widthOfSidebar && widthOfSidebar <= 500) {
          localStorage.setItem('sideBarWidth', String(widthOfSidebar));
          dispatch(setSideBarWidth(widthOfSidebar));
        } else if (widthOfSidebar <= defaultSidebarWidth && widthOfSidebar >= 100) {
          localStorage.setItem('sideBarWidth', String(defaultSidebarWidth));
          dispatch(setSideBarWidth(defaultSidebarWidth));
        } else if (widthOfSidebar < 100 && isVisibleHideSideBarButton) {
          localStorage.setItem('sideBarWidth', String(narrowSidebarWidth));
          dispatch(setSideBarWidth(narrowSidebarWidth));
        } else if (widthOfSidebar >= 500) {
          localStorage.setItem('sideBarWidth', String(500));
          dispatch(setSideBarWidth(500));
        }
      }
    },
    [isResizing]
  );

  React.useEffect(() => {
    window.addEventListener('mousemove', resize);
    window.addEventListener('mouseup', stopResizing);
    return () => {
      window.removeEventListener('mousemove', resize);
      window.removeEventListener('mouseup', stopResizing);
    };
  }, [isResizing]);

  React.useEffect(() => {
    const hasInviteRoom = available_rooms.length === 1 && available_rooms[0]?.invite_id;
    
    if (((available_rooms.length === 1 && isFirstRender.current && !hasInviteRoom) || selectedKeys[0] === 'data-analytics') && isVisibleHideSideBarButton) {
      localStorage.setItem('sideBarWidth', String(narrowSidebarWidth));
      dispatch(setSideBarWidth(narrowSidebarWidth));
      isFirstRender.current=false;
    }
    if (!isVisibleHideSideBarButton && sideBarWidth === narrowSidebarWidth) {
      localStorage.setItem('sideBarWidth', String(defaultSidebarWidth));
      dispatch(setSideBarWidth(defaultSidebarWidth));
    } else if (isVisibleHideSideBarButton) {
      dispatch(setSideBarWidth(Number(localStorage.getItem('sideBarWidth')) || defaultSidebarWidth));
    }
  }, [available_rooms, selectedKeys]);

  const onChangeTab = (key: string) => {    
    localStorage.setItem('prevTab', key);
    if (key === 'users') {
      setSelectedTab(key);
    } else {
      dispatch(setSelectedKeys([key]))
    }

    if (key === 'rooms') {
      if (dataRoom?.id && !isBlockedRoom) {
        const filterParams = serializeFiltersToUrl(new URLSearchParams(), appliedFilters).toString();
        const filterQuery = filterParams ? `?${filterParams}` : '';
        dataRoom?.access_terms_status == 'waiting_for_approve'
        ? navigate(`/room/${dataRoom.custom_url || dataRoom?.id}/nda`)
        : navigate(`/room/${dataRoom.custom_url || dataRoom?.id}/documents${filterQuery}`)
      } else {
        navigate(`/${key}`)
      };
    } else if (key === 'chat') {
      if (dataRoom?.id) navigate(`/room/${dataRoom.custom_url || dataRoom?.id}/${key}`);
      else if (!dataRoom?.id) navigate(`/global-chats/`);
      else if (available_rooms[0]?.id)
        navigate(`/room/${available_rooms[0]?.custom_url || available_rooms[0]?.id}/${key}`);
    }

  };

  // функционал не успользуется - закомментировал
  // const privatedRooms = ['143d9fd4-96b7-46e8-819f-9515a04eaff9'] // не трогать
  // const isAdmin = available_rooms.filter(room => privatedRooms.includes(room.id))[0]?.permissions?.can_edit_roles // и это тоже
  
  const badgeLength = [...roomAllChats, ...globalChats].filter((chat) => chat.unviewed_amount > 0).length;
  const items: TabsProps['items'] = [
    {
      key: 'rooms',
      label: t('sidebar.shortRooms'),
      children: <RoomsTab
        clearRedux={() => dispatch(clearReduxFunction({}))}
        widthSB={sideBarWidth}
      />,
    },
    {
      key: 'chat',
      label: (
        <Badge
          offset={[badgeLength > 99 ? 15 : 10, -2]}
          style={{ fontSize: '10px' }}
          size='small'
          count={badgeLength}
          overflowCount={99}
        >
          <span style={{color: !Boolean(userData?.id) ? 'rgba(0, 0, 0, 0.25)' : 'inherit'}}>{t('sidebar.chats')}</span>
        </Badge>
      ),
      children: <ChatTab />,
      disabled: isBlockedRoom || !Boolean(userData?.id),
    },
    {
      key: 'contacts',
      label: t('sidebar.contacts'),
      children: <ContactsTab clearRedux={() => dispatch(clearReduxFunction({}))} />,
      disabled: !Boolean(userData?.id),
    },
  ];

  const defaultTabs = items?.reduce(
    (allTabs: string[], tab: any) => [String(tab.key), ...allTabs],
    [],
  );

  React.useEffect(() => {
    if (selectedKeys[0].includes('chat')) {
      setSelectedTab('chat')
    } else if (defaultTabs?.includes(selectedKeys[0])) {
      setSelectedTab(selectedKeys[0])
    } else if (isRoomsSection) {
      setSelectedTab('rooms')
    }
  }, [selectedKeys]);

  return (
    <div
      className={classes.sider}
      ref={sidebarRef}
      style={{width: isMobile ? '100vw' : sideBarWidth}}
    >
      <div
        className={classes.tabsWrap}
      >
        <Tabs
          className={classes.tabsStyles}
          activeKey={selectedTab}
          defaultActiveKey={'documents'}
          size='small'
          items={items}
          onChange={onChangeTab}
        />
        
        {isVisibleHideSideBarButton && <Button
          className={classes.collapseIcon}
          icon={ sideBarWidth === narrowSidebarWidth ? <Assets.DoubleRightIcon /> : <Assets.DoubleLeftIcon />}
          onClick={collapseClick}
        />}

      </div>
      <div
          className={classes.resizer}
          onMouseDown={startResizing}
          data-testid='sidebar-resizer'
      />
    </div>
  );
}
